import { CampaignAdsGenerateSpinner } from '@/components/campaign/overview/ads-generate-spinner';
import useAreAllTasksCompleted from '@/hooks/useAreAllTasksCompleted';
import { campaignQueryOptions, campaignTaskTrackingQueryOptions } from '@/lib/query-options';
import { campaignTaskTrackingAtom } from '@/store';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { useEffect, useState, useRef } from 'react';

export const Route = createFileRoute('/_authenticated/new/_layout/$campaignslug/generating')({
  component: CampaignGeneratingComponent,
});

function CampaignGeneratingComponent() {
  const { campaignslug } = Route.useParams();
  const [minTimeReached, setMinTimeReached] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();

  const { data: campaign } = useSuspenseQuery(campaignQueryOptions(campaignslug));
  const setCampaignTaskTracking = useSetAtom(campaignTaskTrackingAtom);
  // Only poll for task tracking - this is the one query we want to keep active
  const { data: campaignTaskTracking } = useQuery(campaignTaskTrackingQueryOptions(campaign.id));

  setCampaignTaskTracking(campaignTaskTracking);
  // Check if all tasks are completed
  const areAllTasksCompleted = useAreAllTasksCompleted();

  // Set up the time-based navigation restriction based on task tracking created_at
  useEffect(() => {
    // Clear any existing timer when this effect runs
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!campaignTaskTracking || !campaignTaskTracking.created_at) {
      return;
    }
    
    const taskCreationTime = new Date(campaignTaskTracking.created_at).getTime();
    const oneMinuteAfterCreation = taskCreationTime + 60000; // 1 minute in milliseconds
    const currentTime = Date.now();
    
    
    if (currentTime >= oneMinuteAfterCreation) {
      setMinTimeReached(true);
    } else {
      // If not yet time, set a timeout for the remaining time
      const timeRemaining = oneMinuteAfterCreation - currentTime;
      
      timerRef.current = setTimeout(() => {
        setMinTimeReached(true);
      }, timeRemaining);
    }

    // Clean up timer on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [campaignTaskTracking]);

  // Navigate when all tasks are complete AND minimum time has passed - ONCE ONLY
  const navigate = useNavigate();
  useEffect(() => {

    if (areAllTasksCompleted && minTimeReached) {
      // Invalidate notifications so the new campaign notification appears
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
      navigate({ to: `/campaigns/google/${campaignslug}` });
    }
  }, [areAllTasksCompleted, minTimeReached, campaignTaskTracking, queryClient]);

  const handleBackClick = () => {
    navigate({ to: '/campaigns' });
  };

  return (
    <CampaignAdsGenerateSpinner 
      showBackButton={true}
      onBackClick={handleBackClick}
      backButtonText="Back to Campaigns"
    />
  );
}
